import { isEmpty } from "lodash";
import { Box, H5, Divider } from "orcs-design-system";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";

import TreeViewNext from "src/shared/TreeViewNext";
import urlPaths, { url, matchPaths } from "src/consts/urlPaths";
import { EVENT_TRACKING } from "src/consts/eventTracking";
import { trackEvent } from "src/services/segment";
import { buildTagsTree, filterTagTypes, type TagNavNode } from "./index.logic";
import { getSidebarTagTreeLinkTo } from "./getTagTreeLinkTo";
import TagTreeHeader from "./TagTreeHeader";
import useLoadTags from "./useLoadTags";
import useUpdateCurrentTag from "./useUpdateCurrentTagListener";

type TagsNavigationProps = {
  tagTypesConfig?: Record<string, unknown> | undefined;
  isSelected?: boolean | undefined;
};

const TagsNavigation = ({
  tagTypesConfig,
  isSelected,
}: TagsNavigationProps) => {
  const [treeData, setTreeData] = useState<TagNavNode[]>([]);
  const toggleCountRef = useRef(0);
  const activeTagId = matchPaths()?.params?.["id"] as string | undefined;

  const targetTagTypes = useMemo(
    () => filterTagTypes(tagTypesConfig),
    [tagTypesConfig]
  );

  const { activeTag, tags, loadTags, tagsLoading } = useLoadTags({
    activeTagId,
    isSelected,
    tagTypesConfig,
  });

  const history = useHistory();

  const getTagLinkTo = useCallback(
    (node: TagNavNode) => getSidebarTagTreeLinkTo(node),
    []
  );

  const customizeTreeHeader = useCallback(
    ({ node }: { node: TagNavNode }, helpers: { onActivate: () => void }) => (
      <TagTreeHeader
        getTagLinkTo={getTagLinkTo}
        node={node}
        onActivate={() => helpers.onActivate()}
      />
    ),
    [getTagLinkTo]
  );

  useUpdateCurrentTag(setTreeData);

  const onChevronToggle = useCallback(
    async (treeNode: TagNavNode, isOpen: boolean) => {
      if (!treeNode.isTagType) {
        return;
      }

      const { id } = treeNode;
      setTreeData((tree) =>
        tree.map((node) =>
          node.id === id ? { ...node, toggled: isOpen } : node
        )
      );
      toggleCountRef.current += 1;

      if (isOpen) {
        trackEvent(EVENT_TRACKING.TAG_CLICKED, {
          action: "tag_tree_navigation_expanded",
          totalToggles: toggleCountRef.current,
        });
        await loadTags(id as string, true);
      } else {
        trackEvent(EVENT_TRACKING.TAG_CLICKED, {
          action: "tag_tree_navigation_collapsed",
          totalToggles: toggleCountRef.current,
        });
      }
    },
    [loadTags]
  );

  const onNodeSelected = useCallback(
    async (treeNode: TagNavNode) => {
      const { id, isTagValue, isLoadMore, isTagTypeDesc } = treeNode;

      if (isTagTypeDesc || treeNode.loading || treeNode.isEmptyStateMessage) {
        return;
      } else if (isLoadMore) {
        await loadTags(id as string);
      } else if (treeNode.isTagType) {
        trackEvent(EVENT_TRACKING.TAG_CLICKED, {
          action: "tag_tree_navigation_tag_type_selected",
        });
        // Tag-type SPA navigation is handled by TagTreeHeader Router Link.
      } else if (isTagValue) {
        trackEvent(EVENT_TRACKING.TAG_CLICKED, {
          action: "tag_tree_navigation_selected",
        });
        history.push(url(urlPaths.TAG_DETAILS, { id }));
      }
    },
    [history, loadTags]
  );

  useEffect(() => {
    if (isEmpty(targetTagTypes)) {
      return;
    }

    setTreeData((tree) => {
      return buildTagsTree({
        tagTypes: targetTagTypes,
        tags,
        tree,
        activeTag,
        activeTagId,
        tagTypesConfig,
        tagsLoading,
      });
    });
  }, [
    activeTag,
    activeTagId,
    tagTypesConfig,
    tags,
    tagsLoading,
    targetTagTypes,
  ]);

  return (
    <Box data-testid="tags-navigation" mb="r">
      <H5 fontWeight="bold" data-testid="heading">
        Tags
      </H5>
      <Box my="r">
        <Divider light />
      </Box>
      {isEmpty(treeData) ? (
        <H5>No tags data available</H5>
      ) : (
        <TreeViewNext<TagNavNode>
          data={treeData}
          onNodeSelected={onNodeSelected}
          onChevronToggle={onChevronToggle}
          suppressOnNodeSelectedWhenCollapsingBranch
          customizeTreeHeader={customizeTreeHeader}
          activeNodeId={activeTagId}
          getNodeOpenState={(node) =>
            typeof node.toggled === "boolean" ? node.toggled : undefined
          }
          syncOpenStateOnDataChange={false}
          openByDefault={false}
          isNodeDisabled={(node) =>
            !!node.isTagTypeDesc || !!node.loading || !!node.isEmptyStateMessage
          }
        />
      )}
    </Box>
  );
};

export default TagsNavigation;
